import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import get from 'lodash/get';
import debounce from 'lodash/debounce';

import { getYouIcon } from './legend';
import './bargraph.css';

const MAX_BAR_WIDTH = 600;
const MIN_SCALE_LABEL_WIDTH = 40;

const messages = defineMessages({
  noSchoolData: {
    id: 'hubs.overview.academic-match.noSchoolData',
    defaultMessage: 'No school data available',
  },
  noNationalData: {
    id: 'hubs.overview.academic-match.noNationalData',
    defaultMessage: 'No national data available',
  },
  noData: {
    id: 'hubs.overview.academic-match.noData',
    defaultMessage: 'No data available',
  },
  yourSchool: {
    id: 'hubs.overview.academic-match.yourSchool',
    defaultMessage: 'YOUR SCHOOL',
  },
  national: {
    id: 'hubs.overview.academic-match.national',
    defaultMessage: 'NATIONAL',
  },
});

class BarGraph extends React.PureComponent {
  static propTypes = {
    graphData: PropTypes.shape({
      option: PropTypes.string,
      plotData: PropTypes.shape({
        low: PropTypes.number,
        high: PropTypes.number,
        peerLow: PropTypes.number,
        peerHigh: PropTypes.number,
      }),
      nationalData: PropTypes.shape({
        low: PropTypes.number,
        high: PropTypes.number,
      }),
      schoolData: PropTypes.shape({
        low: PropTypes.number,
        high: PropTypes.number,
      }),
      userData: PropTypes.number,
      useGpaConcordance: PropTypes.bool,
      usePsat: PropTypes.bool,
    }),
  };

  state = { windowWidth: window.innerWidth };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  /* istanbul ignore next */
  handleResize = debounce(() => {
    this.setState({ windowWidth: window.innerWidth });
  }, 500);

  getPlotLeft = (data, usePeers = false) => {
    const plotData = get(this.props, 'graphData.plotData');
    /* istanbul ignore if */
    if (!plotData || !data) {
      return 0;
    } else if (usePeers) {
      return (
        ((data.low - plotData.peerLow) * 100) / (plotData.peerHigh - plotData.peerLow)
      );
    }
    return ((data.low - plotData.low) * 100) / (plotData.high - plotData.low);
  };

  getPlotWidth = (data, usePeers = false) => {
    const plotData = get(this.props, 'graphData.plotData');
    /* istanbul ignore if */
    if (!plotData || !data) {
      return 0;
    }
    if (usePeers) {
      return ((data.high - data.low) * 100) / (plotData.peerHigh - plotData.peerLow);
    }
    return ((data.high - data.low) * 100) / (plotData.high - plotData.low);
  };

  getPlotStyles = (data, usePeers = false) => {
    const styles = {};
    const left = this.getPlotLeft(data, usePeers);
    const width = this.getPlotWidth(data, usePeers);
    styles.left = `${left > 0 ? left : 0}%`;
    styles.width = `${width > 0 ? width : 0}%`;
    if (left <= 0 && width <= 0) {
      styles.display = 'none';
    }
    /* istanbul ignore next */
    if (left > 99 && left <= 100) {
      styles.left = `calc(${left}% - 4px)`;
    }
    return styles;
  };

  isShowLowScale = (data) => {
    const plotData = get(this.props, 'graphData.plotData');
    /* istanbul ignore if */
    if (!data || !plotData) {
      return false;
    }
    const lowGapPerc = ((data.low - plotData.low) * 100) / (plotData.high - plotData.low);
    return this.getPixelsConv(lowGapPerc) > MIN_SCALE_LABEL_WIDTH || data.high <= 0;
  };

  isShowHighScale = (data) => {
    const plotData = get(this.props, 'graphData.plotData');
    /* istanbul ignore if */
    if (!data || !plotData) {
      return false;
    }
    const highGapPerc =
      ((plotData.high - data.high) * 100) / (plotData.high - plotData.low);
    return this.getPixelsConv(highGapPerc) > MIN_SCALE_LABEL_WIDTH || data.high <= 0;
  };

  isShowUserScaleOnLeft = (userData) => {
    const plotData = get(this.props, 'graphData.plotData');
    /* istanbul ignore if */
    if (!plotData) {
      return false;
    }

    const highGapPerc =
      (Math.abs(plotData.high - userData) * 100) / (plotData.high - plotData.low);
    return this.getPixelsConv(highGapPerc) < MIN_SCALE_LABEL_WIDTH;
  };

  getPixelsConv = (perc) => {
    let width = MAX_BAR_WIDTH;
    /* istanbul ignore if */
    if (this.state.windowWidth * 0.7 < width) {
      width = this.state.windowWidth * 0.7;
    }
    return width * (perc / 100);
  };

  render() {
    if (!this.props.graphData || !this.props.graphData.plotData) {
      return null;
    }

    const {
      plotData,
      userData,
      option,
      useGpaConcordance,
      usePsat,
    } = this.props.graphData;
    const nationalData = {
      low: Math.max(get(this.props, 'graphData.nationalData.low', 0), plotData.low),
      high: Math.min(get(this.props, 'graphData.nationalData.high', 0), plotData.high),
    };

    const usePeers =
      useGpaConcordance &&
      option.toLowerCase().includes('gpa') &&
      !Number.isNaN(plotData.peerLow) &&
      !Number.isNaN(plotData.peerHigh);
    const schoolPlotDataLow = usePeers ? plotData.peerLow : plotData.low;
    const schoolPlotDataHigh = usePeers ? plotData.peerHigh : plotData.high;

    const schoolData = {
      low: Math.max(get(this.props, 'graphData.schoolData.low', 0), schoolPlotDataLow),
      high: Math.min(get(this.props, 'graphData.schoolData.high', 0), schoolPlotDataHigh),
    };

    const nationalWidth = this.getPixelsConv(this.getPlotWidth(nationalData));
    const nationalLeft = this.getPlotLeft(nationalData);
    let schoolWidth = this.getPixelsConv(this.getPlotWidth(schoolData, usePeers));
    let schoolLeft = this.getPlotLeft(schoolData, usePeers);
    let userLeft = this.getPlotLeft({ low: userData }, usePeers);
    userLeft = userLeft <= 0 ? null : Math.min(userLeft, 100);

    const showMainHighScale = this.isShowHighScale(nationalData);
    const showMainLowScale = this.isShowLowScale(nationalData);
    const showUserScaleOnLeft = this.isShowUserScaleOnLeft(userData);
    const dataExists =
      nationalWidth > 0 || nationalLeft > 0 || schoolWidth > 0 || schoolLeft > 0;
    const schoolDataPlottable =
      !Number.isNaN(schoolData.low) && !Number.isNaN(schoolData.high);

    return (
      <div className="barGraph-container" data-cy="bargraph-container">
        <div className="plot-container">
          {dataExists ? (
            <React.Fragment>
              <div className="national-plot" style={this.getPlotStyles(nationalData)}>
                {nationalWidth > 70 ? (
                  <span className="national-plot-text">
                    <FormattedMessage {...messages.national} />
                  </span>
                ) : null}
                <div className="national-plot-scale low-scale-line high-scale-line">
                  <div className="plot-low-label">{nationalData.low}</div>
                  {nationalWidth > 0 && (
                    <div className="plot-high-label">{nationalData.high}</div>
                  )}
                </div>
              </div>
              {nationalWidth <= 0 && nationalLeft <= 0 ? (
                <div className="national-plot-negative">
                  <FormattedMessage {...messages.noNationalData} />
                </div>
              ) : null}
              {schoolDataPlottable && (
                <div
                  className="school-plot"
                  style={this.getPlotStyles(schoolData, usePeers)}
                >
                  {schoolWidth > 100 ? (
                    <span className="school-plot-text">
                      <FormattedMessage {...messages.yourSchool} />
                    </span>
                  ) : null}
                  <div
                    className={`school-plot-scale low-scale-line ${
                      schoolWidth > 0 ? 'high-scale-line' : ''
                    }`}
                  >
                    <div className="plot-low-label">{schoolData.low}</div>
                    {schoolWidth > 0 && (
                      <div className="plot-high-label">{schoolData.high}</div>
                    )}
                  </div>
                </div>
              )}
              {schoolWidth <= 0 && schoolLeft <= 0 ? (
                <div className="school-plot-negative">
                  <FormattedMessage {...messages.noSchoolData} />
                </div>
              ) : null}
            </React.Fragment>
          ) : (
            <div className="empty-data-plot">
              <FormattedMessage {...messages.noData} />
            </div>
          )}
          {userLeft ? (
            <div className="user-data-plot" style={{ left: `calc(${userLeft}% - 5px)` }}>
              {getYouIcon({ top: '23px', right: '100%', position: 'relative' })}
              <div
                className={`user-data-plot-hor-scale
                  ${showUserScaleOnLeft ? 'reversed' : ''}
                  ${usePsat ? 'psat' : ''}
                `}
              >
                {userData}
                {usePsat && <font>&nbsp;(PSAT)</font>}
              </div>
            </div>
          ) : null}
        </div>
        <div
          className={`main-plot-scale
                  ${showMainLowScale ? 'low-scale-line' : ''}
                  ${showMainHighScale ? 'high-scale-line' : ''}
                `}
        >
          {showMainLowScale && <div className="plot-low-label">{plotData.low}</div>}
          {showMainHighScale && <div className="plot-high-label">{plotData.high}</div>}
        </div>
        {usePeers && (
          <div className={'peer-plot-scale low-scale-line high-scale-line'}>
            <div
              className="plot-low-label peer-low-label"
              style={{ right: `calc(${100}% + 3px)` }}
            >
              {plotData.peerLow}
            </div>
            <div
              className="plot-high-label peer-high-label"
              style={{ left: `calc(${100}% + 3px)` }}
            >
              {plotData.peerHigh}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default injectIntl(BarGraph);
